import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader, SimpleGrid, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IoListOutline, IoMegaphoneOutline } from 'react-icons/io5';
import {
  sellerFinanceApi,
  type SellerAdvertisingCabinet,
} from '@modules/seller-finance/api/seller-finance.api';
import { SellerAdvertisingHistoryModal } from '@modules/seller-finance/components/SellerAdvertisingHistoryModal';
import { QuietIconButton } from '@shared/ui';
import { formatPrice } from '@shared/utils';

function errorMessage(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const message = (e as { response?: { data?: { message?: string } } }).response?.data
      ?.message;
    if (message) return message;
  }
  return e instanceof Error ? e.message : 'Ошибка запроса';
}

function invoiceStatusLabel(status: string): string {
  if (status === 'ISSUED') return 'ожидает оплаты';
  if (status === 'PAID') return 'оплачен';
  return 'отменён';
}

function refundStatusLabel(status: string): string {
  if (status === 'PENDING') return 'ожидает перевода';
  if (status === 'PAID') return 'выплачен';
  return 'отменён';
}

export function SellerAdvertisingSection({
  sellerId,
  shopName,
}: {
  sellerId: number;
  shopName?: string | null;
}) {
  const queryClient = useQueryClient();
  const [historyOpen, setHistoryOpen] = useState(false);
  const query = useQuery({
    queryKey: ['seller-finance', 'advertising', sellerId],
    queryFn: () => sellerFinanceApi.getSellerAdvertising(sellerId),
  });

  const refresh = () =>
    queryClient.invalidateQueries({
      queryKey: ['seller-finance', 'advertising', sellerId],
    });

  const run = useMutation({
    mutationFn: (action: () => Promise<unknown>) => action(),
    onSuccess: () => refresh(),
    onError: (e: unknown) => {
      notifications.show({
        title: 'Не удалось обновить рекламный баланс',
        message: errorMessage(e),
        color: 'red',
      });
    },
  });

  const data: SellerAdvertisingCabinet | undefined = query.data;
  const invoices = data?.invoices ?? [];
  const refunds = data?.refunds ?? [];

  return (
    <>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <div className="seller-dashboard-metric seller-dashboard-metric--accent">
          <div className="seller-dashboard-metric__top">
            <Text className="seller-dashboard-metric__title">Рекламный баланс</Text>
            <span className="seller-dashboard-metric__dot" aria-hidden>
              <IoMegaphoneOutline />
            </span>
          </div>
          <Text className="seller-dashboard-metric__value">
            {query.isPending ? '…' : formatPrice(data?.advertisingBalanceRub ?? 0)}
          </Text>
          <Text className="seller-dashboard-metric__hint">
            Отдельно от суммы к выплате. Оплата счёта — аванс, клики списываются здесь.
          </Text>
        </div>
        <div className="seller-dashboard-metric seller-dashboard-metric--blue">
          <div className="seller-dashboard-metric__top">
            <Text className="seller-dashboard-metric__title">Потрачено на рекламу</Text>
            <QuietIconButton
              icon={<IoListOutline size={16} />}
              onClick={() => setHistoryOpen(true)}
            >
              История
            </QuietIconButton>
          </div>
          <Text className="seller-dashboard-metric__value">
            {query.isPending ? '…' : formatPrice(data?.totalSpentRub ?? 0)}
          </Text>
          <Text className="seller-dashboard-metric__hint">
            Списания за клики за всё время
          </Text>
        </div>
      </SimpleGrid>

      <SellerAdvertisingHistoryModal
        seller={historyOpen ? { sellerId, shopName } : null}
        onClose={() => setHistoryOpen(false)}
      />

      <section className="seller-dashboard-card seller-finance-card">
        <div className="seller-dashboard-card__head">
          <h2 className="seller-dashboard-card__title">Счета и возвраты</h2>
        </div>
        {query.isPending ? (
          <div className="seller-finance-mobile-empty">
            <Loader size="sm" />
          </div>
        ) : invoices.length === 0 && refunds.length === 0 ? (
          <p className="seller-finance-adv-empty">Счетов и возвратов пока нет.</p>
        ) : (
          <div className="seller-finance-adv-list">
            {invoices.map((invoice) => (
              <div key={invoice.id} className="seller-finance-adv-row">
                <div className="seller-finance-adv-row__main">
                  <span className="seller-finance-adv-row__title">{invoice.number}</span>
                  <span className="seller-finance-adv-row__meta">
                    {formatPrice(invoice.amountRub)} · {invoiceStatusLabel(invoice.status)}
                  </span>
                </div>
                {invoice.status === 'ISSUED' ? (
                  <div className="seller-finance-adv-row__actions">
                    <button
                      type="button"
                      className="seller-detail-action seller-detail-action--accent"
                      disabled={run.isPending}
                      onClick={() =>
                        run.mutate(() => sellerFinanceApi.payAdvertisingInvoice(invoice.id))
                      }
                    >
                      Оплачен
                    </button>
                    <button
                      type="button"
                      className="seller-detail-action"
                      disabled={run.isPending}
                      onClick={() =>
                        run.mutate(() => sellerFinanceApi.cancelAdvertisingInvoice(invoice.id))
                      }
                    >
                      Отменить
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
            {refunds.map((refund) => (
              <div key={refund.id} className="seller-finance-adv-row">
                <div className="seller-finance-adv-row__main">
                  <span className="seller-finance-adv-row__title">Возврат №{refund.id}</span>
                  <span className="seller-finance-adv-row__meta">
                    {formatPrice(refund.amountRub)} · {refundStatusLabel(refund.status)}
                  </span>
                </div>
                {refund.status === 'PENDING' ? (
                  <div className="seller-finance-adv-row__actions">
                    <button
                      type="button"
                      className="seller-detail-action seller-detail-action--accent"
                      disabled={run.isPending}
                      onClick={() =>
                        run.mutate(() => sellerFinanceApi.payAdvertisingRefund(refund.id))
                      }
                    >
                      Переведён
                    </button>
                    <button
                      type="button"
                      className="seller-detail-action"
                      disabled={run.isPending}
                      onClick={() =>
                        run.mutate(() => sellerFinanceApi.cancelAdvertisingRefund(refund.id))
                      }
                    >
                      Отменить
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
